import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
  Card,
} from "react-bootstrap";
import {
  AiOutlineUser,
  AiOutlineLock,
  AiOutlineEye,
  AiOutlineEyeInvisible,
} from "react-icons/ai";
import {
  FaExclamationTriangle,
  FaArrowRight,
  FaCalculator,
  FaUserPlus,
  FaSignature,
  FaClock,
  FaBolt,
  FaUsers,
  FaChartLine,
  FaLock,
  FaExternalLinkAlt,
} from "react-icons/fa";
import loginEmailPassword from "../functions/loginEmailPassword";

const Login = ({ authError }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  // Sync external errors (from App.js) to local error state
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError("");
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCargando(true);
    setError("");

    if (!email || !password) {
      setError("Por favor completa todos los campos.");
      setCargando(false);
      return;
    }

    if (!email.includes("@")) {
      setError("Por favor ingresa un correo electrónico válido.");
      setCargando(false);
      return;
    }

    try {
      const emailNormalizado = email.toLowerCase().trim();
      await loginEmailPassword(emailNormalizado, password);
    } catch (err) {
      console.error("Error en login:", err);

      if (err.code === "auth/user-not-found") {
        setError("No existe una cuenta con este correo electrónico.");
      } else if (err.code === "auth/wrong-password") {
        setError("La contraseña es incorrecta.");
      } else if (err.code === "auth/invalid-email") {
        setError("El formato del correo electrónico no es válido.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Demasiados intentos fallidos. Intenta más tarde.");
      } else if (err.code === "auth/invalid-credential") {
        setError("Credenciales inválidas. Verifica tu correo y contraseña.");
      } else if (
        err.message &&
        err.message.includes("No se encontró ningún registro")
      ) {
        setError("Usuario no registrado en el sistema administrativo.");
      } else {
        setError("Error al iniciar sesión. Contacta al soporte técnico.");
      }
    }
    setCargando(false);
  };

  const servicios = [
    {
      icon: <FaClock size={24} />,
      titulo: "Gestión de Asistencia y Ausencias",
      descripcion: "Registro con integración biométrica y geolocalización",
      color: "#FF6B35",
      url: "https://miboleta.net.pe/asistencia-detalle",
    },
    {
      icon: <FaCalculator size={24} />,
      titulo: "Gestión de Nómina",
      descripcion: "Cálculo automático y reportes gubernamentales",
      color: "#4ECDC4",
      url: "https://miboleta.net.pe/planilla-detalle",
    },
    {
      icon: <FaUserPlus size={24} />,
      titulo: "Gestión de Onboarding Digital",
      descripcion: "Automatización de procesos de ingreso",
      color: "#45B7D1",
      url: "https://miboleta.net.pe/onboarding-detalle",
    },
    {
      icon: <FaSignature size={24} />,
      titulo: "Gestión de Contratos",
      descripcion:
        "Creación, firma digital y seguimiento con validez legal completa",
      color: "#96CEB4",
      url: "https://miboleta.net.pe/contratos-detalle",
    },
  ];

  const estadisticas = [
    { numero: "10,000+", label: "Empleados Gestionados", icon: <FaUsers /> },
    { numero: "6", label: "Agentes IA Integrados", icon: <FaBolt /> },
    { numero: "99.9%", label: "Uptime Garantizado", icon: <FaChartLine /> },
    { numero: "24/7", label: "Soporte Técnico", icon: <FaClock /> },
  ];

  const handleServiceClick = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Partículas animadas de fondo */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: `
          radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.2) 0%, transparent 50%)
        `,
          zIndex: 1,
        }}
      ></div>

      {/* Overlay de carga */}
      {cargando && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              borderRadius: "20px",
              padding: "40px",
              textAlign: "center",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <Spinner
              animation="border"
              style={{
                width: "50px",
                height: "50px",
                marginBottom: "20px",
                color: "#667eea",
              }}
            />
            <div
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#2d3748",
                marginBottom: "8px",
              }}
            >
              Iniciando sesión...
            </div>
            <div style={{ fontSize: "14px", color: "#718096" }}>
              Verificando credenciales y cargando datos
            </div>
          </div>
        </div>
      )}

      <Container
        fluid
        style={{ position: "relative", zIndex: 2, minHeight: "100vh" }}
      >
        <Row className="min-vh-100">
          {/* Panel izquierdo - Login */}
          <Col
            xs={12}
            lg={5}
            xl={4}
            className="d-flex align-items-center justify-content-center p-0"
          >
            <div
              style={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                borderRadius: "0",
                minHeight: "100vh",
                width: "100%",
                display: "flex",
                alignItems: "center",
                boxShadow: "0 0 50px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div className="w-100 px-5 py-4">
                {/* Logo y header */}
                <div className="text-center mb-5">
                  <div
                    style={{
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      borderRadius: "20px",
                      padding: "25px",
                      display: "inline-block",
                      marginBottom: "30px",
                      boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
                    }}
                  >
                    <img
                      src="https://miboleta.net.pe/assets/img/logo-white.png"
                      alt="Mi Boleta"
                      className="img-fluid"
                      style={{ maxWidth: "200px" }}
                    />
                  </div>

                  <h1
                    style={{
                      color: "#2d3748",
                      fontSize: "2.2rem",
                      fontWeight: "700",
                      marginBottom: "12px",
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Panel Administrativo
                  </h1>
                  <p
                    style={{
                      color: "#718096",
                      fontSize: "1.1rem",
                      fontWeight: "400",
                      margin: 0,
                    }}
                  >
                    Gestión integral de recursos humanos
                  </p>
                </div>

                {/* Formulario */}
                {error && (
                  <Alert
                    variant="danger"
                    className="d-flex align-items-center mb-4"
                    style={{
                      borderRadius: "15px",
                      border: "none",
                      background: "linear-gradient(135deg, #ff6b6b, #ee5a52)",
                      color: "white",
                      boxShadow: "0 8px 25px rgba(255, 107, 107, 0.3)",
                    }}
                  >
                    <FaExclamationTriangle className="me-3" size={20} />
                    <span style={{ fontSize: "0.95rem", fontWeight: "500" }}>
                      {error}
                    </span>
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label
                      style={{
                        color: "#2d3748",
                        fontWeight: "600",
                        fontSize: "0.95rem",
                        marginBottom: "12px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <AiOutlineUser
                        size={20}
                        className="me-2"
                        style={{ color: "#667eea" }}
                      />
                      Correo electrónico
                    </Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="tu-email@empresa.com"
                      value={email}
                      onChange={handleEmailChange}
                      disabled={cargando}
                      autoComplete="email"
                      style={{
                        borderRadius: "12px",
                        border: "2px solid #e2e8f0",
                        padding: "16px 20px",
                        fontSize: "1rem",
                        transition: "all 0.3s ease",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#667eea";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(102, 126, 234, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e2e8f0";
                        e.target.style.boxShadow =
                          "0 2px 10px rgba(0, 0, 0, 0.05)";
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label
                      style={{
                        color: "#2d3748",
                        fontWeight: "600",
                        fontSize: "0.95rem",
                        marginBottom: "12px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <AiOutlineLock
                        size={20}
                        className="me-2"
                        style={{ color: "#667eea" }}
                      />
                      Contraseña
                    </Form.Label>
                    <div style={{ position: "relative" }}>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••••"
                        value={password}
                        onChange={handlePasswordChange}
                        disabled={cargando}
                        autoComplete="current-password"
                        style={{
                          borderRadius: "12px",
                          border: "2px solid #e2e8f0",
                          padding: "16px 50px 16px 20px",
                          fontSize: "1rem",
                          transition: "all 0.3s ease",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#667eea";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(102, 126, 234, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#e2e8f0";
                          e.target.style.boxShadow =
                            "0 2px 10px rgba(0, 0, 0, 0.05)";
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: "15px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          color: "#718096",
                          cursor: "pointer",
                          padding: "5px",
                        }}
                      >
                        {showPassword ? (
                          <AiOutlineEyeInvisible size={20} />
                        ) : (
                          <AiOutlineEye size={20} />
                        )}
                      </button>
                    </div>
                  </Form.Group>

                  <Button
                    type="submit"
                    disabled={cargando}
                    className="w-100 mb-4"
                    style={{
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      border: "none",
                      borderRadius: "12px",
                      padding: "16px",
                      fontSize: "1.1rem",
                      fontWeight: "600",
                      transition: "all 0.3s ease",
                      boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                      position: "relative",
                      overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                      if (!cargando) {
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow =
                          "0 12px 35px rgba(102, 126, 234, 0.4)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!cargando) {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow =
                          "0 8px 25px rgba(102, 126, 234, 0.3)";
                      }
                    }}
                  >
                    {cargando ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Iniciando sesión...
                      </>
                    ) : (
                      <>
                        Iniciar Sesión
                        <FaArrowRight className="ms-2" />
                      </>
                    )}
                  </Button>

                  <div className="text-center">
                    <small style={{ color: "#718096", fontSize: "0.9rem" }}>
                      <FaLock className="me-1" />
                      Conexión segura protegida por SSL
                    </small>
                  </div>
                </Form>
              </div>
            </div>
          </Col>

          {/* Panel derecho - Información */}
          <Col xs={12} lg={7} xl={8} className="p-0 d-none d-lg-block">
            <div
              style={{
                background:
                  "linear-gradient(135deg, rgba(0,0,0,0.3), rgba(0,0,0,0.1))",
                minHeight: "100vh",
                color: "white",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Contenido principal */}
              <div className="h-100 d-flex flex-column justify-content-center p-5">
                {/* Header */}
                <div className="text-center mb-5">
                  <h2
                    style={{
                      fontSize: "3rem",
                      fontWeight: "700",
                      marginBottom: "20px",
                      textShadow: "0 2px 20px rgba(0,0,0,0.3)",
                    }}
                  >
                    Revoluciona tu Gestión de RRHH
                  </h2>
                  <p
                    style={{
                      fontSize: "1.3rem",
                      opacity: "0.9",
                      maxWidth: "600px",
                      margin: "0 auto",
                      lineHeight: "1.6",
                    }}
                  >
                    La plataforma más completa para la gestión moderna de
                    recursos humanos
                  </p>
                </div>

                {/* Servicios */}
                <Row className="mb-5">
                  {servicios.map((servicio, index) => (
                    <Col md={6} key={index} className="mb-4">
                      <Card
                        style={{
                          background: "rgba(255, 255, 255, 0.1)",
                          backdropFilter: "blur(10px)",
                          border: "1px solid rgba(255, 255, 255, 0.2)",
                          borderRadius: "15px",
                          transition: "all 0.3s ease",
                          cursor: "pointer",
                        }}
                        onClick={() => handleServiceClick(servicio.url)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-5px)";
                          e.currentTarget.style.background =
                            "rgba(255, 255, 255, 0.15)";
                          e.currentTarget.style.boxShadow =
                            "0 10px 30px rgba(0, 0, 0, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.background =
                            "rgba(255, 255, 255, 0.1)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        <Card.Body className="p-4">
                          <div className="d-flex align-items-start">
                            <div
                              style={{
                                width: "50px",
                                height: "50px",
                                background: servicio.color,
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginRight: "15px",
                                flexShrink: 0,
                                color: "white",
                              }}
                            >
                              {servicio.icon}
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <h5
                                    style={{
                                      color: "white",
                                      fontWeight: "600",
                                      marginBottom: "8px",
                                      fontSize: "1.1rem",
                                    }}
                                  >
                                    {servicio.titulo}
                                  </h5>
                                  <p
                                    style={{
                                      color: "rgba(255, 255, 255, 0.8)",
                                      marginBottom: 0,
                                      fontSize: "0.9rem",
                                      lineHeight: "1.4",
                                    }}
                                  >
                                    {servicio.descripcion}
                                  </p>
                                </div>
                                <FaExternalLinkAlt
                                  size={16}
                                  style={{
                                    color: "rgba(255, 255, 255, 0.6)",
                                    marginLeft: "10px",
                                    flexShrink: 0,
                                  }}
                                />
                              </div>
                              <div
                                style={{
                                  marginTop: "12px",
                                  padding: "8px 12px",
                                  background: "rgba(255, 255, 255, 0.1)",
                                  borderRadius: "8px",
                                  fontSize: "0.8rem",
                                  color: "rgba(255, 255, 255, 0.9)",
                                  fontWeight: "500",
                                }}
                              >
                                Haz clic para conocer más detalles
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>

                {/* Estadísticas */}
                <Row className="text-center">
                  {estadisticas.map((stat, index) => (
                    <Col xs={6} lg={3} key={index} className="mb-4">
                      <div
                        style={{
                          background: "rgba(255, 255, 255, 0.1)",
                          borderRadius: "15px",
                          padding: "25px 15px",
                          backdropFilter: "blur(10px)",
                          border: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                      >
                        <div
                          style={{
                            color: "#4ECDC4",
                            fontSize: "2rem",
                            marginBottom: "10px",
                          }}
                        >
                          {stat.icon}
                        </div>
                        <div
                          style={{
                            fontSize: "1.8rem",
                            fontWeight: "700",
                            marginBottom: "5px",
                          }}
                        >
                          {stat.numero}
                        </div>
                        <div
                          style={{
                            fontSize: "0.9rem",
                            opacity: "0.8",
                          }}
                        >
                          {stat.label}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Login;
